import { Component, Inject } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { IRetailerOption } from "src/app/models";
import {
  ProductField,
  ProductUnitField,
  ProductUnitType,
  ProductCardSection,
  translate as translateEnum
} from "src/app/enum";
import {
  ProductService,
  ProductValidity
} from "src/app/_services/product.service";
import { SystemClvNamePipe, TranslateEnumPipe } from "src/app/pipes";
import { stripHtml } from "src/app/util/fn";
import { IRetailChain } from "src/app/cards/product/product-info/product-info.model";
import { Location } from '@angular/common';

@Component({
  selector: "product-validation-dialog",
  host: { class: "dialog" },
  templateUrl: "./product-validation.dialog.html",
  styleUrls: ["./product-validation.dialog.scss"]
})
export class ProductValidationDialogComponent {
  public formGroup: FormGroup;

  public validationInstructions = {
    prelude: "cards.products.product_validation.info_array.prelude",
    text: [
      "cards.products.product_validation.info_array.text1",
      "cards.products.product_validation.info_array.text2",
      "cards.products.product_validation.info_array.text3"
    ]
  };

  public retailerOptions: IRetailChain[] = [];
  public productUnits = [];
  public productId: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: any,
    public dialogRef: MatDialogRef<ProductValidationDialogComponent>,
    private _productService: ProductService,
    private _productValidity: ProductValidity,
    private _translateService: TranslateService,
    private _translateEnumPipe: TranslateEnumPipe,
    private _location: Location
  ) {
    const { routeData, productId, validateId } = this._data;

    this.retailerOptions = routeData?.productCard?.formData?.retailChains || [];
    this.productUnits = routeData?.productCard?.units || [];
    this.productId = productId;

    let selectedRetailers = this.validatedRetailers || [];

    if (validateId) {
      const validateRetailer = this.retailerOptions.find(
        (x) => x.id === validateId
      );

      if (validateRetailer) {
        selectedRetailers = [validateRetailer];
      }
      
      // Remove '/validate' from the end of the path and clear query params
      let path = window.location.pathname;
      path = path.replace(/\/validate$/, "");
      this._location.replaceState(path);
    }

    this.formGroup = new FormGroup({
      retailers: new FormControl(selectedRetailers)
    });
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public get showSummary() {
    return this._productValidity.shouldValidate;
  }

  public get hasSummary() {
    return this._productValidity.shouldValidate && (
      this._productValidity.requiredByRetailChainNames || 
      this._productValidity.get()?.invalidProductFieldValues ||
      this._productValidity.get()?.invalidProductUnitFieldValues ||
      this._productValidity.get()?.pricesInfoFieldValues
    ) ? true : false;
    // return this._productValidity.requiredByRetailChainNames ? true : false;
  }

  public get validatedRetailers() {
    return this._productValidity.validatedRetailers;
  }

  public get invalidUnitFields() {

    const invalidUnitIds = this._productValidity.invalidUnitIds;

    if (!invalidUnitIds?.length) {
      return null;
    }

    return invalidUnitIds
      .map((x) => {
        const unitType = this.productUnits.find((u) => u.id === x)?.type;
        return (
          this._translateEnumPipe.transform(unitType?.id, "ProductUnitType") +
          " (" +
          this._productValidity
            .invalidUnitFieldsById(x)
            ?.map((key) =>
              stripHtml(
                translateEnum(ProductUnitField, key, this._translateService)
              )
            )
            .join(", ") +
          ")"
        );
      })
      .join(", ");
  }

  public get missingPricesInfoFields() {
    return this._productValidity.missingPricesInfoFieldValues;
  }

  public get isProductValid() {
    return this._productValidity.isProductValid;
  }

  public get groupedInvalidFields() {
    const invalidFields = this._productValidity.get()?.invalidProductFieldValues;
    if (!invalidFields) {
      return [];
    }

    const sectionTranslationMap = {
      [ProductCardSection.BaseInfo]: 'cards.products.product_base_info.title',
      [ProductCardSection.ExtraParameters]: 'cards.products.product_description_fields.title',
      [ProductCardSection.Attributes]: 'cards.products.product_attributes.title',
      [ProductCardSection.AlcoholInfo]: 'cards.products.product_alcohol.title',
      [ProductCardSection.SeafoodInfo]: 'cards.products.product_fish.title',
      [ProductCardSection.ElectronicsInfo]: 'cards.products.product_electronics.title',
      [ProductCardSection.MarkingAndNutritionInfo]: 'cards.products.product_certificate_info_and_nutrition_info.title',
      [ProductCardSection.NutritionClaims]: 'cards.products.product_certificate_info_and_nutrition_info.title',
      [ProductCardSection.NutritionFacts]: 'cards.products.product_certificate_info_and_nutrition_info.title',
      [ProductCardSection.VitaminsAndMinerals]: 'cards.products.product_certificate_info_and_nutrition_info.title',
      [ProductCardSection.AdditionalInfoAndAttachments]: 'cards.products.product_additional_info_and_instructions.title',
      [ProductCardSection.ExpiryInfo]: 'cards.products.product_storage_info.title',
      [ProductCardSection.ProductUnits]: 'cards.products.product_packaging.title',
      [ProductCardSection.ProductImages]: 'cards.products.product_images.title',
      [ProductCardSection.PriceSchemes]: 'cards.products.product_pricelist.title',
      [ProductCardSection.AssortmentSchemes]: 'cards.products.product_status.title',
      [ProductCardSection.StoreSchemes]: 'cards.products.product_store.title',
      [ProductCardSection.MeatInfo]: 'cards.products.product_meat.title',
      [ProductCardSection.MovieInfo]: 'cards.products.product_movie_info.title',
      [ProductCardSection.MusicInfo]: 'cards.products.product_music_info.title',
      [ProductCardSection.BookInfo]: 'cards.products.product_book_info.title',
      [ProductCardSection.GameInfo]: 'cards.products.product_game_info.title',
      [ProductCardSection.MedicationInfo]: 'cards.products.product_medication_info.title',
      [ProductCardSection.ClientPricelists]: 'cards.products.product_pricelist.title_clientprices',
      [ProductCardSection.History]: 'cards.history.title'
    };

    const groupedFields = Object.keys(invalidFields).map(sectionKey => {
      const section = parseInt(sectionKey) as ProductCardSection;
      const sectionTitle = this._translateService.instant(sectionTranslationMap[section] || sectionKey);
      const fields = invalidFields[sectionKey].map(fieldKey =>
        stripHtml(translateEnum(ProductField, fieldKey, this._translateService))
      );
      return { sectionTitle, fields };
    });

    return groupedFields;
  }

  private _checkPricesInfoFieldValues(
    retailerIds = this.formGroup.get("retailers").value.map((x) => x.id)
  ) {
    const { routeData } = this._data;
    const { pricesInfo } = routeData?.productCard || {};
    const { supplierBasePrices, supplierRetailerPrices } = pricesInfo || {};

    let arr = [];

    const hasPublicSalesPrice = supplierBasePrices?.some(
      (x) => x.isCurrent && x.publicSalesPrice
    );

    let hasSupplierRetailerPrice = false;
    for (let i = 0; i < retailerIds.length; i++) {
      const retailerId = retailerIds[i];
      const supplierRetailerPrice = supplierRetailerPrices?.[retailerId];

      if (!supplierRetailerPrice) {
        continue;
      }
      if (
        supplierRetailerPrice?.isCurrent &&
        supplierRetailerPrice?.publicSalesPrice
      ) {
        hasSupplierRetailerPrice = true;
        break;
      }
    }

    if (!hasPublicSalesPrice && !hasSupplierRetailerPrice) {
      arr.push("cards.products.product_validation.fields.current_price_list");
    }

    return arr;
  }

  public isValidateLoading = false;
  public validateRetailers() {
    this.isValidateLoading = true;
    const retailers = this.formGroup.get("retailers").value;
    const retailerIds = retailers?.map((x) => x.id);
    this._productValidity.setValidatedRetailers(retailers);
    this._productService
      .validateRetailChainFieldRules(this.productId, retailerIds)
      .result(
        null,
        (res) => {
          this._productValidity.shouldValidate = true;

          let validity = res;

          Object.assign(validity, {
            pricesInfoFieldValues: this._checkPricesInfoFieldValues()
          });

          this._productValidity.set(validity);

          this.isValidateLoading = false;
        },
        (error) => {
          this.isValidateLoading = false;
        }
      );
  }

  public resetValidation() {
    this._productService.emptyRetailChainFieldRules();
    // this._productValidity.emptyValidatedRetailers();
  }

  public onCloseAfterValidation() {
    this.closeDialog();
    setTimeout(_ => {
      this.resetValidation();
    }, 50);
  }

  ngOnInit() {
    const { validateOnOpen } = this._data;
    if (validateOnOpen) {
      setTimeout((_) => {
        this.validateRetailers();
      });
    }
    // Subscribe to changes in the retailers form control to reset validation
    this.formGroup.get('retailers').valueChanges.subscribe(() => {
      this.resetValidation();
    });
  }

}
